import styled from '@emotion/styled';
import classNames from 'classnames';
import { FC, PropsWithChildren, useCallback, useRef } from 'react';

import { useBoolean } from '../../hooks';
import Flex from '../Flex';
import Icon from '../Icon';
import Menu, { MenuProps } from '../Menu';
import { SelectOption } from '../Select';

// Local Typings
export interface MenuItemButton {
  label: string;
  onClick: () => void;
}
export interface MenuButtonProps extends Omit<MenuProps, 'anchorEl' | 'isOpen' | 'onChange' | 'options' | 'value'> {
  buttons: MenuItemButton[];
  className?: string;
  disabled: boolean | undefined;
  isLoading: boolean | undefined;
}

// Local Variables
export const menuButtonWidth = 30;
const StyledHr = styled.hr<{ isDisabled: boolean }>(props => ({
  backgroundColor: props.isDisabled ? props.theme.colors.v3_gray_400 : props.theme.semanticColors.brand_600,
  border: 0,
  height: props.theme.components.button.size.lg,
  left: 0,
  position: 'absolute',
  transition: '250ms',
  width: 2,
}));
const StyledButton = styled.button(props => ({
  alignItems: 'center',
  backgroundColor: props.theme.semanticColors.button_default,
  border: `2px solid ${props.theme.semanticColors.button_default}`,
  borderBottomRightRadius: props.theme.components.button.borderRadius,
  borderLeft: 0,
  borderTopRightRadius: props.theme.components.button.borderRadius,
  bottom: 0,
  display: 'flex',
  height: props.theme.components.button.size.lg,
  outline: 0,
  paddingLeft: props.theme.spacing * 2,
  position: 'absolute',
  right: 0,
  top: 0,
  transition: '250ms',
  width: menuButtonWidth,

  /* eslint-disable sort-keys,@typescript-eslint/naming-convention */
  // This order is important https://www.w3.org/TR/CSS21/selector.html#dynamic-pseudo-classes
  '&:focus': {
    boxShadow: `0px 0px 0px 2px ${props.theme.semanticColors.brand_200}`,
  },

  '&:hover': {
    backgroundColor: props.theme.semanticColors.button_hover,
    borderColor: props.theme.semanticColors.button_hover,
  },

  '&:active': {
    backgroundColor: props.theme.semanticColors.button_active,
    borderColor: props.theme.semanticColors.button_active,
  },

  '&:disabled': {
    backgroundColor: props.theme.colors.v3_navy_300,
    borderColor: props.theme.colors.v3_navy_300,
    cursor: 'not-allowed',
  },
  /* eslint-enable sort-keys,@typescript-eslint/naming-convention */
}));

// Component Definition
const MenuButton: FC<PropsWithChildren<MenuButtonProps>> = ({
  buttons,
  children,
  className,
  disabled,
  isLoading,
  values,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleChange = useCallback((value: SelectOption['value']) => {
    const label = String(value);
    const button = buttons.find(b => b.label === label);

    button?.onClick();
  },
  [buttons]);

  const {
    handleToggle,
    isTrue: isOpen,
  } = useBoolean();

  return (
    <Flex
      className={classNames(className, 'menu-button')}
      position="relative"
      ref={containerRef}
      width="min-content"
    >
      {children}

      <StyledButton
        disabled={!!disabled || !!isLoading}
        onClick={handleToggle}
        type="button"
      >
        <StyledHr isDisabled={!!disabled} />

        <Icon
          color={disabled ? 'disabled' : 'white'}
          name="caret-down"
          size="xxs"
        />
      </StyledButton>

      {containerRef.current && !!buttons.length && (
        <Menu
          align="left"
          onChange={handleChange}
          options={buttons.map(button => ({
            label: button.label,
            value: button.label,
          }))}
          value=""
          {...props}
          anchorEl={containerRef.current}
          isOpen={isOpen}
          onClose={handleToggle}
        />
      )}
    </Flex>
  );
};

export default MenuButton;
